import { formatISO } from 'date-fns';
import { getDecimal } from "../config/utils";

export const getCaseLabel = (court) => {
    if (!court) return 'Case Number';
    if (court.caseNumberLabel) return court.caseNumberLabel;
    if (court.nonCourtOrganization) return 'Account Number';
    return 'Case Number';
}

export const getCitationLabel = (court) => {
    if (!court) return 'Citation Number';
    if (court.citationNumberLabel) return court.citationNumberLabel;
    if (court.nonCourtOrganization) return 'Reference Number';
    return 'Citation Number';
}

export const getLicenseLabel = (court) => {
    if (court && court.licensePlateLookup) {
        return 'License Plate Number';
    } else {
        return '';
    }
}

const getTrafficSchoolAmount = (balance, court) => {
    const opts = balance.trafficSchoolOptions.filter(o => o.optionId === balance.trafficSchoolId);
    if (opts && opts.length > 0) {
        const configs = court.trafficSchoolConfigs.filter(c => c.optionType === opts[0].optionType);
        if (configs && configs.length > 0 && configs[0].requireBalanceFees) {
            return parseFloat(getDecimal(balance.courtAmount + opts[0].optionAmount, false));
        } else {
            return opts[0].optionAmount;
        }
    }
    return balance.courtAmount;
}

const getTrafficSchoolDescription = (balance, court) => {
    const opts = balance.trafficSchoolOptions.filter(o => o.optionId === balance.trafficSchoolId);
    if (opts && opts.length > 0) {
        if (opts[0].optionDescription) return opts[0].optionDescription;
        const configs = court.trafficSchoolConfigs.filter(c => c.optionType === opts[0].optionType);
        if (configs && configs.length > 0 && configs[0].optionDescription) return configs[0].optionDescription;
    }
    return 'Traffic School';
}

const getBalanceServiceFee = (balance) => {
    if (balance.paymentPlan && balance.paymentPlan.planPaymentServiceFee && balance.paymentPlan.planPaymentServiceFee > 0) {
        return balance.paymentPlan.planPaymentServiceFee;
    } else if (balance.paymentPlans && balance.paymentPlans.length > 0) {
        let fee = 0;
        balance.paymentPlans.forEach(p => {
            if (p.planPaymentServiceFee && p.planPaymentServiceFee > 0 && fee <= 0) {
                fee = p.planPaymentServiceFee;
            }
        });
        fee = parseFloat(getDecimal(fee, false));
        if (fee > 0.00) {
            return fee;
        }
    }
    return 0;
}

const getPlanCases = (plan) => {
    const caseArray = [];
    plan.balances.forEach(b => caseArray.push(b.caseNumber));
    return  caseArray.join();
}

export const getCourtFromCourtId = (courtId, courts) => {
    if (courts && courts.length > 0) {
        const courtsOut = courts.filter(ct => ct.courtId === courtId);
        if (courtsOut && courtsOut.length > 0) return courtsOut[0];
    }
    return null;
}

export const validateSearch = (tabVal, form, dob, court) => {
    switch (tabVal) {
        case '1':
            if (!form.caseNo) return `The ${getCaseLabel(court)} is required`;
            break;
        case '2':
            if (!form.citationNo) return `The ${getCitationLabel(court)} is required`;
            break;
        case '3':
            if (!form.lastName) {
                return `The Last Name and either the First Name or the DOB are required`;
            } else if (!dob && !form.firstName) return `The Last Name and either the First Name or the DOB are required`;
            break;
        case '4':
            if (!form.licensePlateNumber ) return `The ${getLicenseLabel(court)} is required`;
            break;
    }
}

export const getSearchRequest = (tabVal, form, dob, court) => {
    if (form.licensePlateNumber && form.licensePlateNumber.length) {
        return {
            courtId: court.courtId,
            licensePlateNumber: form.licensePlateNumber.replace(/%/g, '')

        };
    }
    return {
        courtId: court.courtId,
        caseNumber: (form.caseNo ? form.caseNo : null),
        citationNumber: (form.citationNo ? form.citationNo : null),
        dob: (dob ? formatISO(dob, {representation: 'date'}) : null),
        firstName: (form.firstName ? form.firstName : null),
        lastName: (form.lastName ? form.lastName : null),
        fromAdmin: true
    };
};

export const checkMultipleParties = (balanceData) => {
    let hasMultiple = false;
    if (balanceData.balances && balanceData.balances.length > 0) {
        const bals = [];
        balanceData.balances.forEach(b => {
            const key = `${b.party.id}`;
            bals[key] = b;
        });
        const test = Object.keys(bals).length;
        if (test > 1) hasMultiple = true;
    }
    return hasMultiple;
}

export const getPartyData = (balanceData) => {
    let data = [];
    balanceData.balances.forEach(b => {
        const key = `${b.party.id}`;
        if (!data[key]) {
            const partyRec = {
                key: key,
                party: b.party,
                balances: []
            }
            partyRec.balances.push(b);
            data[key] = partyRec;
        } else {
            data[key].balances.push(b);
        }
    })
    return data;
}

export const toPaymentBalance = (balance, court) => {
    if (balance.ineligibleForPayment && !balance.bondId) return null;
    const paymentPlanId = balance.paymentPlanId;
    const out = {
        key: balance.refId,
        refId: balance.refId,
        description: balance.description,
        courtAmount: balance.courtAmount,
        balancePaymentAmount: balance.partialAmt && balance.partialAmt > 0 ? balance.partialAmt : balance.courtAmount,
        partialAmt: balance.partialAmt,
        partyName: balance.party.partyName,
        partyId: balance.party.id,
        caseId: balance.caseId,
        chargeId: balance.chargeId,
        caseNumber: balance.caseNumber,
        citationNumber: balance.citationNumber,
        licensePlateNumber: balance.licensePlateNumber,
        balanceType: balance.balanceType,
        baseBalanceType: balance.baseBalanceType,
        dob: balance.party.dob,
        minimumPartialAmount: balance.minimumPartialAmount,
        paymentPlanId: paymentPlanId,
        bondId: balance.bondId,
        serviceFee: null,
        disposed: balance.disposed,
        ineligible: balance.ineligibleForPayment
    };
    if (balance.trafficSchoolId) {
        out.trafficSchoolId = balance.trafficSchoolId;
        out.trafficSchoolType = balance.trafficSchoolType;
        out.balancePaymentAmount = getTrafficSchoolAmount(balance, court);
        out.description += ` (${getTrafficSchoolDescription(balance, court)})`
    } else if (out.bondId && balance.bondAmt && balance.bondAmt > 0) {
        out.balancePaymentAmount = balance.bondAmt;
        out.description = `Bond for: ${balance.description}`;
    }
    const serviceFee = getBalanceServiceFee(balance);
    if (serviceFee > 0) {
        out.balancePaymentAmount = parseFloat(getDecimal(out.balancePaymentAmount + serviceFee, false));
        out.serviceFee = serviceFee;
    }
    return out;
}

export const toPaidPlan = (planIn) => {
    const planOut = {
        key: planIn.planId,
        planId: planIn.planId,
        planDescription: planIn.planDescription,
        planPaymentAmount: 0.00,
        planPaymentServiceFee: planIn.planPaymentServiceFee,
        installments: [],
        installmentCount: 0,
        balances: [],
        cases: getPlanCases(planIn)
    }
    if (planIn.installments) {
        planIn.installments.forEach(i => {
            if (i.checked) {
                planOut.planPaymentAmount += i.installmentAmount
                planOut.installments.push(Object.assign({}, i));
            }
        });
        if (planIn.planPaymentServiceFee) {
            planOut.planPaymentAmount += planIn.planPaymentServiceFee;
        }
        planOut.planPaymentAmount = parseFloat(getDecimal(planOut.planPaymentAmount, false));
        if (planIn.balances && planOut.planPaymentAmount > 0.00) {
            let balanceAmt = 0.00;
            planIn.balances.forEach((b, idx) => {
                if (balanceAmt < planOut.planPaymentAmount) {
                    const balOut = Object.assign({}, b);
                    balOut.description = `Payment plan: ${balOut.description}`;
                    if (((balOut.balancePaymentAmount + balanceAmt) > planOut.planPaymentAmount) || idx === (planIn.balances.length - 1)) {
                        balOut.balancePaymentAmount = planOut.planPaymentAmount - balanceAmt;
                    }
                    balOut.balancePaymentAmount = parseFloat(getDecimal(balOut.balancePaymentAmount));
                    balanceAmt += balOut.balancePaymentAmount;
                    planOut.balances.push(balOut);
                }
            })
        }
    }
    return planOut;
}
